import { Col, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import AuthWrapper from 'components/base/AuthWrapper';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import cisMenyApi from 'services/cisMeny';
import cisStatyApi from 'services/cisStaty';
import {
    IPodpisIDs,
    dokumentLoginSMS,
    dokumentPodpisLoginGet,
    dokumentPodpisLoginSMSlogin
} from 'services/dokumenty/sms';

import { EPodTypdokumentuPrikaz, EStavPodpisu, ETypDokumentu } from 'enums/dokumentyEnums';
import routePathEnum from 'enums/routePathEnum';

interface IDokumentyLoginSMS {}

const DokumentyLoginSMS: React.FC<IDokumentyLoginSMS> = () => {
    const { token } = useContext(Context);
    const { setLevel } = useProfily();
    const { navigateWorkaround } = useNavigateWorkaround();
    const [loading, setLoading] = useState<boolean>(false);
    const [errMessage, setErrMessage] = useState<string>('');
    const [showInput, setShowInput] = useState<boolean>(false);
    const [smsCodeErr, setSmsCodeErr] = useState<boolean>(false);
    const [smsResp, setSmsResp] = useState<IPodpisIDs>();
    const [isComplete, setIsComplete] = useState<boolean>(false);
    const { hash } = useParams<{ hash: string }>();

    useEffect(() => {
        dokumentPodpisLoginGet(hash!).then(resp => {
            (resp.stav === EStavPodpisu.PODEPSANO ||
                resp.stav === EStavPodpisu.OVERENO ||
                resp.stav === EStavPodpisu.NEOVERENO) &&
                setIsComplete(true);
            setSmsResp(resp);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        token && navigateWorkaround(routePathEnum.DOKUMENTY_SMS);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const poslatSms = () => {
        setLoading(true);
        dokumentLoginSMS(hash!)
            .catch(() => setErrMessage('RESPONSE_ERROR_400'))
            .then(() => setShowInput(true))
            .finally(() => setLoading(false));
    };

    return (
        <AuthWrapper>
            {isComplete ? (
                <>
                    <Title
                        level={4}
                        text={
                            (Object.keys(EPodTypdokumentuPrikaz).includes(smsResp?.podtyp || '')
                                ? 'DOKUMENTY_PRIKAZ_'
                                : 'TYP_SMLOUVY_') + smsResp?.podtyp
                        }
                    />
                    <Paragraph
                        text="PODPIS_ODKAZ_SUCC_TEXT"
                        style={{
                            display: 'block'
                        }}
                    />
                </>
            ) : smsResp?.platnost ? (
                <>
                    {!showInput ? (
                        <LoadingWrapper loading={loading}>
                            <Title text="PODPIS_DOKUMENTU" level={5} />
                            <Paragraph
                                style={{
                                    display: 'block'
                                }}
                                text="PODPIS_DOKUMENTU_TEXT"
                            />
                            <Col>
                                <Row className="button-row">
                                    <ButtonComponent
                                        buttontext="ZASLAT_SMS_KOD"
                                        onClick={poslatSms}
                                        id="pxKlicPokracovatBtn"
                                    />
                                </Row>
                            </Col>
                        </LoadingWrapper>
                    ) : (
                        <>
                            {showInput && (
                                <>
                                    <Title text="PODPIS_DOKUMENTU" level={5} />
                                    <Paragraph text="IDENTIFIKACE_OSOBY_KOD" />
                                    <div
                                        style={{
                                            margin: 10
                                        }}
                                    >
                                        <FormWrapper
                                            onFinish={vals => {
                                                setSmsCodeErr(false);
                                                setLoading(true);
                                                dokumentPodpisLoginSMSlogin(hash!, vals.kod)
                                                    .then(resp => {
                                                        if (!resp.token) {
                                                            setSmsCodeErr(true);
                                                        } else {
                                                            setLevel('dokumenty', {
                                                                selectedZadost: {
                                                                    iddok: smsResp.iddok,
                                                                    idosoby: smsResp.idosoby,
                                                                    stavpodpisu: smsResp.stav,
                                                                    podtyp: smsResp.podtyp,
                                                                    typ: Object.keys(EPodTypdokumentuPrikaz).includes(
                                                                        smsResp.podtyp
                                                                    )
                                                                        ? ETypDokumentu.PRIKAZY
                                                                        : ETypDokumentu.SMLOUVY
                                                                },
                                                                dokumentyMenuReady: true
                                                            });
                                                            setLevel('token', resp.token);
                                                            cisStatyApi().then(({ data }) => {
                                                                setLevel('cisStaty', data);
                                                            });
                                                            cisMenyApi().then(({ data }) => {
                                                                setLevel('cisMeny', data);
                                                            });
                                                        }
                                                    })
                                                    .catch(() => setSmsCodeErr(true))
                                                    .finally(() => setLoading(false));
                                            }}
                                        >
                                            <InputComponent label="CODE" name="kod" />
                                            <Row className="button-row">
                                                <ButtonComponent
                                                    buttontext="POKRACOVAT"
                                                    id="pxKlicPokracovatBtn"
                                                    htmlType="submit"
                                                    loading={loading}
                                                    extramessage={smsCodeErr ? 'SMS_INVALID' : ''}
                                                />

                                                {errMessage && <TranslateText>{errMessage}</TranslateText>}
                                            </Row>
                                        </FormWrapper>
                                    </div>
                                    <Row>
                                        <TranslateText>SMS_RESEND_TXT</TranslateText>
                                        <ButtonComponent
                                            id="sendSMSbtn"
                                            style={{
                                                marginTop: -8,
                                                marginLeft: 10
                                            }}
                                            htmlType="button"
                                            type="primary"
                                            buttontext="SMS_RESEND_BTN"
                                            loading={loading}
                                            onClick={poslatSms}
                                        />
                                    </Row>
                                </>
                            )}
                        </>
                    )}
                </>
            ) : (
                <Title level={5} text="PRAVO_ZADOST_EXPIRED" />
            )}
        </AuthWrapper>
    );
};

export default DokumentyLoginSMS;
